import { format } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { STICKY_HEADERS } from "@/constants/featureFlags";
import { secondaryButtonClasses } from "@/constants/ui";
import { useCalendar } from "@/contexts/calendarContext";
import { filterRoomsByAmenities } from "@/utils/roomAmenityFilter";
import { useMember } from "@/contexts/memberContext";
import { TimeSlotClickInterceptor } from "@/interceptors/booking";
import { getPaletteBySwatch } from "@/lib/paletteBySwatch";
import { occursOnDate } from "@/lib/recurrence";
import { sameUtcDay, startOfUtcWeek } from "@/lib/calendarDay";
import { getCalendarViewMaxDate } from "@/utils/bookingDateBounds";
import { AmenityService } from "@/services/AmenityService";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { MakeABookingDialog } from "../../MakeABookingDialog";
import { AmenityBookingColumn } from "./BookingColumn";
import {
  addUtcDays,
  addUtcMonths,
  effectiveDayWidth,
  formatDayHeader,
  getWeekDates,
  layoutBookingTile,
  isSameDay,
} from "./BrickWeekSchedule.utils";
import Bubble from "./Bubble";
import Buffers from "./Buffers";
import { hasAnyBuffer } from "@/utils/bufferFlags";
import { AmenityColumnHeaders, RoomColumnHeaders } from "./ColumnHeaders";
import RoomDialog from "./RoomDialog";
import type { BookingInterceptionResult } from "@/interceptors/booking";
import type { AmenityBooking } from "@/types/AmenityBooking";
import type { Room } from "@/types/Room";

const BrickWeekSchedule = () => {
  const KEY_WIDTH = 60; //px
  const KEY_HEIGHT = 36;
  const TIMES_TO_HIDE_EARLY = ["0030", "0100", "0130", "0200", "0230", "0300", "0330", "0400", "0430", "0500", "0530"];

  const { member } = useMember();
  const {
    isLoading,
    bookings,
    date,
    slug,
    setDate,
    selectedLocation,
    refreshBookings,
    selectedRooms,
    selectedAmenities,
    selectedRoomAmenities,
  } = useCalendar();
  // "Rooms with amenities" filter — narrow the drawn rooms to those having ALL selected amenities.
  const visibleRooms = filterRoomsByAmenities(selectedRooms, selectedRoomAmenities);
  const [hiddenEarly, setHiddenEarly] = useState(true);
  const [amenityBookings, setAmenityBookings] = useState<AmenityBooking[]>([]);
  const [showRoomInfoModal, setShowRoomInfoModal] = useState<boolean>(false);
  const [roomForInfoModal, setRoomForInfoModal] = useState<Room | null>(null);
  const [showBookingDialog, setShowBookingDialog] = useState<boolean>(false);
  const [bookingResult, setBookingResult] = useState<BookingInterceptionResult | null>(null);

  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);

  // Helper to check if a specific time slot is shuttered
  const isTimeSlotShuttered = useCallback((room: Room, date: Date, time: string): boolean => {
    if (!room.shutterUntil) return false;
    const shutterDate = new Date(room.shutterUntil);

    // Check if shutterDate is valid
    if (isNaN(shutterDate.getTime())) {
      console.error("[isTimeSlotShuttered] Invalid shutterUntil date:", room.shutterUntil);
      return false;
    }

    // Parse the time string (HHmm format)
    const hours = parseInt(time.substring(0, 2), 10);
    const minutes = parseInt(time.substring(2, 4), 10);
    const slotDateTime = new Date(date);
    slotDateTime.setHours(hours, minutes, 0, 0);

    // Check if slotDateTime is valid
    if (isNaN(slotDateTime.getTime())) {
      console.error("[isTimeSlotShuttered] Invalid slot date/time:", date, time);
      return false;
    }

    // Time slot is shuttered if it's before the shutterUntil datetime
    const isShuttered = slotDateTime < shutterDate;

    return isShuttered;
  }, []);

  // Load amenity bookings for the current week
  useEffect(() => {
    if (!selectedLocation || !date) return;

    const weekDates = getWeekDates(date);
    const startDate = weekDates[0];
    const endDate = weekDates[weekDates.length - 1];

    const amenityService = new AmenityService();
    amenityService
      .getAmenityBookingsForLocationAndDateRange(selectedLocation.id, startDate, endDate)
      .then((amenityBookingsResult) => {
        setAmenityBookings(amenityBookingsResult);
      });
    // `bookings` is in the deps so the amenity tiles re-fetch whenever the room bookings refresh
    // (e.g. after make-repeat / remove-repeat). Without it the amenity fetch only re-ran on
    // location/date change, so a removed recurrence's amenity kept repeating on the calendar until
    // the user navigated away. (`bookings` is React-Query data — a stable ref until a refetch.)
  }, [selectedLocation, date, bookings]);

  // Guard to prevent scroll sync feedback loops
  const isSyncingScroll = useRef(false);

  const handleTopScroll = () => {
    if (isSyncingScroll.current) return;
    isSyncingScroll.current = true;
    requestAnimationFrame(() => {
      if (topScrollRef.current) {
        const scrollLeft = topScrollRef.current.scrollLeft;
        if (bottomScrollRef.current) bottomScrollRef.current.scrollLeft = scrollLeft;
        if (headerScrollRef.current) headerScrollRef.current.scrollLeft = scrollLeft;
      }
      isSyncingScroll.current = false;
    });
  };

  const handleBottomScroll = () => {
    if (isSyncingScroll.current) return;
    isSyncingScroll.current = true;
    requestAnimationFrame(() => {
      if (bottomScrollRef.current) {
        const scrollLeft = bottomScrollRef.current.scrollLeft;
        if (topScrollRef.current) topScrollRef.current.scrollLeft = scrollLeft;
        if (headerScrollRef.current) headerScrollRef.current.scrollLeft = scrollLeft;
      }
      isSyncingScroll.current = false;
    });
  };

  const handleHideEarlySegments = () => {
    setHiddenEarly(!hiddenEarly);
  };

  const handleShowRoomInfoModal = (room: Room) => {
    console.log("handleShowRoomInfoModal", room);
    setRoomForInfoModal(room);
    setShowRoomInfoModal(!showRoomInfoModal);
  };

  useEffect(() => {
    if (!topScrollRef.current || !bottomScrollRef.current) return;
    const left = getLeftPosition(date || new Date());
    if (typeof left === "number") {
      topScrollRef.current.scrollLeft = left;
      bottomScrollRef.current.scrollLeft = left;
      if (headerScrollRef.current) {
        headerScrollRef.current.scrollLeft = left;
      }
    }
  }, [topScrollRef.current, bottomScrollRef.current]);

  // Helper to get the start of the week (Monday). UTC-anchored (see @/lib/calendarDay) — this used
  // to compute the LOCAL Monday via getDay/setHours, which under BST (or any non-UTC viewer TZ)
  // could disagree with the UTC-anchored `getWeekDates` used for the grid itself, shifting week
  // navigation by a day relative to what's actually rendered.
  function getStartOfWeek(d: Date) {
    return startOfUtcWeek(d);
  }

  // Helper to add days. UTC-anchored (see @/lib/calendarDay / BrickWeekSchedule.utils) — this used
  // to step via LOCAL setDate/getDate, which under BST (or any non-UTC viewer TZ) could disagree
  // with the UTC-anchored `getStartOfWeek` floor above: stepping 7 LOCAL days across the UK
  // spring-forward boundary lands on 23:00 the previous UTC day, which the UTC week floor then
  // re-floors back onto the SAME week — "Next week" got stuck.
  function addDays(d: Date, days: number) {
    return addUtcDays(d, days);
  }

  // Helper to add months. UTC-anchored for the same reason as addDays above.
  function addMonths(d: Date, months: number) {
    return addUtcMonths(d, months);
  }

  // Navigation constraints
  const today = new Date();
  const startOfCurrentWeek = getStartOfWeek(date ?? new Date());
  // const startOfTodayWeek = getStartOfWeek(today);

  // All users can go back 1 year
  const oneYearAgo = addMonths(today, -12);
  const canGoToPreviousWeek = startOfCurrentWeek > getStartOfWeek(oneYearAgo);

  // Calendar VIEW horizon (end of next calendar year) — wider than the bookable window so members
  // can view recurring/slot bookings running into the following year. New bookings stay capped at
  // the role-aware window (server-side R1 + booking-flow validation).
  const maxForwardDate = getCalendarViewMaxDate(today);
  const canGoToNextWeek = startOfCurrentWeek < getStartOfWeek(maxForwardDate);

  // Generate 30-minute segments for a 24-hour day
  const segments: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const time = `${h.toString().padStart(2, "0")}${m.toString().padStart(2, "0")}`;
      segments.push(time);
    }
  }

  const weekDates = getWeekDates(date ?? new Date());

  if (isLoading) {
    return null;
  }

  function getLeftPosition(d: Date): number | undefined {
    // UTC-anchored: `d` may be the raw context `date` (not itself a weekDates member), so compare
    // by UTC calendar day rather than `toDateString()` — under a non-UTC viewer TZ, a local-string
    // comparison against the UTC-midnight `weekDates` entries can miss/mismatch.
    const dayIdx = weekDates.findIndex((wd) => sameUtcDay(wd, d));
    if (dayIdx === -1) return undefined;
    return dayIdx * dynamicDayWidth();
  }

  // if the screen is XL or larger, we calculate the day width, otherwise
  // it always returns the XL_DAY_WIDTH (we will remove DAY_WIDTH)
  // todo: get this from the member settings
  //
  // The member's chosen calendarWidth is a floor-guarded minimum: when there are enough
  // room/amenity columns that the per-column width would drop below MIN_COLUMN_WIDTH_PX (and the
  // room name would silently disappear), the day widens so each column stays legible and the
  // grid scrolls horizontally instead. See effectiveDayWidth.
  function dynamicDayWidth(): number {
    const raw = member?.settings?.calendarWidth || 500;
    const columns = Math.max(1, visibleRooms.length + selectedAmenities.length);
    return effectiveDayWidth(raw, columns);
  }

  // Coloured-header row height adapts to the width each room column gets:
  //  - ≥ 96px per column → horizontal label → compact 32px header (h-8).
  //  - 40–96px per column → vertical label → a more relaxed 80px so rotated names have room
  //    to breathe; matches the three-tier logic in ColumnHeaders.tsx.
  //  - < 40px per column → coloured bar only → compact 32px again.
  const columnsPerDay = Math.max(1, visibleRooms.length + selectedAmenities.length);
  const columnWidthPx = dynamicDayWidth() / columnsPerDay;
  const colouredHeaderHeightPx = columnWidthPx >= 96 || columnWidthPx < 40 ? 32 : 80;

  const handleTimeSlotClick = (event: React.MouseEvent, date: Date, time: string, room?: Room) => {
    if (!member) return;

    console.log("[BrickWeekSchedule] handleTimeSlotClick - bookings from context:", {
      bookingsType: typeof bookings,
      bookingsIsNull: bookings === null,
      bookingsIsArray: Array.isArray(bookings),
      bookingsLength: bookings?.length ?? "N/A",
      date: date.toISOString(),
      time,
      roomId: room?.id,
    });

    const result = TimeSlotClickInterceptor({
      event,
      date,
      time,
      roomId: room?.id,
      room: room || null,
      member,
      location: selectedLocation || null,
      bookings,
    });

    console.log("[BrickWeekSchedule] TimeSlotClickInterceptor result:", {
      status: result.status,
      bookingsInResult: result.bookings?.length ?? "N/A",
    });
    // Add room name to the result if provided
    if (result.details && room?.name) {
      result.details.roomName = room.name;
    }
    setBookingResult(result);
    setShowBookingDialog(true);
  };

  return (
    <>
      {/* <div className="absolute top-4 right-4 bg-indigo-600 text-white text-xs p-2 rounded">
        {elementWidth}px ({Math.round(elementWidth / 7)}px) (DDW: {dynamicDayWidth()}px)
      </div> */}
      <div className="flex flex-col gap-4 p-2">
        <div className={`flex flex-col border rounded-lg ${STICKY_HEADERS ? "" : "overflow-hidden"}`}>
          <div className="flex flex-row justify-between bg-gray-200 p-2 h-16 items-center">
            <Button
              variant="secondary"
              className={secondaryButtonClasses}
              size="sm"
              onClick={() => {
                if (canGoToPreviousWeek) {
                  setDate(addDays(startOfCurrentWeek, -7));
                }
              }}
              disabled={!canGoToPreviousWeek}
            >
              <ChevronLeft />
              Previous Week
            </Button>
            {/* <span>Week commencing {getWeekDates(date ?? new Date())[0].toLocaleDateString("en-GB")}</span> */}
            <Button
              variant="secondary"
              className={secondaryButtonClasses}
              size="sm"
              onClick={() => {
                if (canGoToNextWeek) {
                  setDate(addDays(startOfCurrentWeek, 7));
                }
              }}
              disabled={!canGoToNextWeek}
            >
              Next Week <ChevronRight />
            </Button>
          </div>

          {/* Top scrollbar (fake, syncs with bottom) */}
          <div className="w-full bg-gray-200" data-testid="room-schedule-top-scrollbar">
            <div
              ref={topScrollRef}
              className="bg-gray-200 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              style={{
                marginLeft: `${KEY_WIDTH}px`,
                width: `calc(100% - ${KEY_WIDTH}px)`,
                height: 16,
              }}
              onScroll={handleTopScroll}
            >
              <div
                style={{
                  width: `${dynamicDayWidth() * 7}px`,
                  minWidth: `${dynamicDayWidth() * 7}px`,
                  maxWidth: `${dynamicDayWidth() * 7}px`,
                  height: 1,
                }}
              />
            </div>
          </div>

          {/* Sticky header section - hoisted above scroll container for proper sticky behavior */}
          {STICKY_HEADERS && (
            <div className="sticky top-0 z-40 bg-white shadow-sm">
              <div className="flex flex-row overflow-hidden">
                <div
                  className="flex-shrink-0 text-center border-r border-b bg-gray-50 border-gray-200 border-dotted h-16 pt-4.5"
                  style={{ minWidth: `${KEY_WIDTH}px`, maxWidth: `${KEY_WIDTH}px`, width: `${KEY_WIDTH}px` }}
                >
                  Time
                </div>
                <div
                  ref={headerScrollRef}
                  className="flex-1 min-w-0 overflow-hidden"
                >
                  <div
                    style={{
                      width: `${dynamicDayWidth() * 7}px`,
                      minWidth: `${dynamicDayWidth() * 7}px`,
                      maxWidth: `${dynamicDayWidth() * 7}px`,
                    }}
                  >
                    <div className="flex h-16">
                      {weekDates.map((d, i) => {
                        const isToday = sameUtcDay(d, today);
                        return (
                          <div
                            className={`pt-4.5 border-r border-l border-2 bg-gray-50 border-gray-200 text-center ${
                              isToday ? "tpr-background px-1 rounded-t-sm text-white tpr-border" : ""
                            }`}
                            style={{
                              width: `${dynamicDayWidth()}px`,
                              minWidth: `${dynamicDayWidth()}px`,
                              maxWidth: `${dynamicDayWidth()}px`,
                            }}
                            key={i}
                          >
                            {formatDayHeader(d)}
                          </div>
                        );
                      })}
                    </div>
                    <div
                      className="w-full relative"
                      style={{ height: `${colouredHeaderHeightPx}px` }}
                    >
                      {weekDates.map((d, dayIdx) => (
                        <div
                          key={dayIdx}
                          className="h-full absolute z-20"
                          style={{
                            left: `${getLeftPosition(d)}px`,
                            width: `${dynamicDayWidth()}px`,
                            minWidth: `${dynamicDayWidth()}px`,
                            maxWidth: `${dynamicDayWidth()}px`,
                          }}
                        >
                          <div
                            // Solid backing so translucent room-header shoulder colours composite
                            // over white rather than letting scrolled bookings bleed through the
                            // sticky header (see paletteBySwatch custom /30 shoulders).
                            className="flex flex-row justify-evenly gap-0 bg-white dark:bg-slate-900"
                            style={{ height: `${colouredHeaderHeightPx}px` }}
                          >
                            <RoomColumnHeaders rooms={visibleRooms} date={d} onRoomClick={handleShowRoomInfoModal} />
                            <AmenityColumnHeaders amenities={selectedAmenities} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-row overflow-hidden">
            <div data-testid="room-schedule-key-column" className="flex-shrink-0">
              {!STICKY_HEADERS && (
                <>
                  <div
                    className="text-center border-r border-b bg-gray-50 border-gray-200 border-dotted h-16 pt-4.5"
                    style={{ minWidth: `${KEY_WIDTH}px`, maxWidth: `${KEY_WIDTH}px`, width: `${KEY_WIDTH}px` }}
                  >
                    Time
                  </div>

                  <div
                    className="bg-gray-50 w-full"
                    style={{ height: `${colouredHeaderHeightPx}px` }}
                  >
                    &nbsp;
                  </div>
                </>
              )}

              <div className="flex flex-col">
                {segments.map((time, i) => {
                  if (hiddenEarly && TIMES_TO_HIDE_EARLY.includes(time)) {
                    return null;
                  }
                  // Draw a slightly darker rule at the top of each hour. The bottom of a
                  // `:30` segment sits exactly on the hour boundary, so darken those and keep
                  // the other (half-hour) boundaries the original dotted grey.
                  const isHalfHour = time.endsWith("30");
                  const borderClasses = isHalfHour
                    ? "border-b border-gray-400"
                    : "border-b border-gray-200 border-dotted";
                  // The hour label is rendered as a small pill absolutely positioned at the
                  // top of the `:00` segment so it straddles the darker hour rule. Half-hour
                  // segments render no label at all.
                  const showHourBubble = !isHalfHour && time !== "0000";
                  return (
                    <div
                      key={i}
                      data-time={time}
                      className={`${borderClasses} relative flex items-center justify-center text-xs bg-gray-50 font-medium`}
                      style={{
                        width: `${KEY_WIDTH}px`,
                        height: `${KEY_HEIGHT}px`,
                        minWidth: `${KEY_WIDTH}px`,
                        maxWidth: `${KEY_WIDTH}px`,
                      }}
                    >
                      {time === "0000" ? (
                        <button
                          className="transform pb-1 text-lg hover:text-indigo-600 focus:outline-none"
                          onClick={handleHideEarlySegments}
                          aria-label="Hide early morning times"
                          type="button"
                        >
                          &hellip;
                        </button>
                      ) : null}
                      {showHourBubble && (
                        <span
                          className="absolute left-1/2 -translate-x-1/2 -top-[10px] z-10 rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-semibold text-gray-700 shadow-sm"
                          aria-hidden
                        >
                          {time}
                        </span>
                      )}
                    </div>
                  );
                })}
                <div className="bg-gray-50 h-16">&nbsp;</div>
              </div>
            </div>

            {/* Data columns with bottom scrollbar */}
            <div className="flex-1 min-w-0">
              <div
                data-testid="room-schedule-data-column"
                style={{
                  minWidth: "400px", // fallback min width
                  overflowY: "hidden",
                }}
                className="w-full overflow-x-auto"
                ref={bottomScrollRef}
                onScroll={handleBottomScroll}
              >
                <div
                  className="h-full flex flex-col relative"
                  style={{
                    width: `${dynamicDayWidth() * 7}px`,
                    minWidth: `${dynamicDayWidth() * 7}px`,
                    maxWidth: `${dynamicDayWidth() * 7}px`,
                  }}
                >
                  {!STICKY_HEADERS && (
                    <div>
                      <div data-testid="room-schedule-day-headers" className="flex h-16">
                        {weekDates.map((d, i) => {
                          const isToday = sameUtcDay(d, today);

                          return (
                            <div
                              className={`pt-4.5 border-r border-l border-2 bg-gray-50 border-gray-200 text-center ${
                                isToday ? "tpr-background px-1 rounded-t-sm text-white tpr-border" : ""
                              }`}
                              style={{
                                width: `${dynamicDayWidth()}px`,
                                minWidth: `${dynamicDayWidth()}px`,
                                maxWidth: `${dynamicDayWidth()}px`,
                              }}
                              data-testid={`room-schedule-day-header-${format(d, "yyyy-MM-dd")}`}
                              key={i}
                            >
                              {formatDayHeader(d)}
                            </div>
                          );
                        })}
                      </div>

                      <div
                        className="w-full relative"
                        style={{ height: `${colouredHeaderHeightPx}px` }}
                        data-testid="room-schedule-coloured-room-headers"
                      >
                        {weekDates.map((d, dayIdx) => (
                          <div
                            key={dayIdx}
                            data-testid={`room-schedule-room-headers-day-container-${format(d, "yyyy-MM-dd")}`}
                            className=" h-full absolute z-20"
                            style={{
                              left: `${getLeftPosition(d)}px`,
                              width: `${dynamicDayWidth()}px`,
                              minWidth: `${dynamicDayWidth()}px`,
                              maxWidth: `${dynamicDayWidth()}px`,
                            }}
                          >
                            <div
                              // Solid backing so translucent room-header shoulder colours composite
                              // over white rather than letting scrolled bookings bleed through the
                              // sticky header (see paletteBySwatch custom /30 shoulders).
                              className="flex flex-row justify-evenly gap-1 bg-white dark:bg-slate-900"
                              style={{ height: `${colouredHeaderHeightPx}px` }}
                            >
                              <RoomColumnHeaders rooms={visibleRooms} date={d} onRoomClick={handleShowRoomInfoModal} />
                              <AmenityColumnHeaders amenities={selectedAmenities} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div data-testid="room-schedule-day-schedule" className="flex relative w-full">
                    {/* Data columns for each day */}
                    {weekDates.map((d) => (
                      <div
                        className="flex flex-col"
                        style={{
                          width: `${dynamicDayWidth()}px`,
                          minWidth: `${dynamicDayWidth()}px`,
                          maxWidth: `${dynamicDayWidth()}px`,
                        }}
                        key={d.toISOString()}
                      >
                        {segments.map((time, segIdx) => {
                          if (hiddenEarly && TIMES_TO_HIDE_EARLY.includes(time)) {
                            return null;
                          }
                          // See time-column comment above: darker solid rule below `:30`
                          // segments (= top of next hour), dotted grey elsewhere.
                          const isHalfHour = time.endsWith("30");
                          const borderClasses = isHalfHour
                            ? "border-b border-gray-400"
                            : "border-b border-gray-200 border-dotted";
                          return (
                            <div
                              data-testid={`room-schedule-time-segment-${time}-${format(d, "yyyy-MM-dd")}`}
                              key={`${segIdx}-${format(d, "yyyy-MM-dd")}`}
                              data-time={time}
                              className={`${borderClasses} flex items-center justify-center text-xs bg-white hover:bg-amber-100 hover:cursor-pointer`}
                              style={{
                                height: `${KEY_HEIGHT}px`,
                                minHeight: `${KEY_HEIGHT}px`,
                                maxHeight: `${KEY_HEIGHT}px`,
                              }}
                              onClick={(e) => handleTimeSlotClick(e, d, time)}
                            >
                              &nbsp;
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    <>
                      {weekDates.map((d, dayIdx) => (
                        <div
                          key={dayIdx}
                          data-testid={`room-schedule-bookings-day-container-${format(d, "yyyy-MM-dd")}`}
                          className="h-full absolute z-20 pointer-events-none"
                          style={{
                            left: `${getLeftPosition(d)}px`,
                            width: `${dynamicDayWidth()}px`,
                            minWidth: `${dynamicDayWidth()}px`,
                            maxWidth: `${dynamicDayWidth()}px`,
                          }}
                        >
                          <div className="flex flex-row h-full justify-evenly gap-0">
                            {/* Room booking columns */}
                            {selectedRooms.map((room) => {
                              const _bg = getPaletteBySwatch(room.swatch)?.transparent || "bg-gray-100/10";
                              const visibleSegments = hiddenEarly
                                ? segments.filter((t) => !TIMES_TO_HIDE_EARLY.includes(t))
                                : segments;
                              // occursOnDate handles every case:
                              //   - non-recurring → genesis-date equality
                              //   - explicit recurrence → rrule expansion
                              //   - slot booking whose `recurrence` document is null/missing →
                              //     synthesised weekly-on-genesis-weekday (see lib/recurrence.ts).
                              // The previous `if (b.recurrence)` short-circuit treated a null
                              // recurrence as non-recurring and quietly hid every future
                              // occurrence of slot bookings — exactly the WBDTXB symptom.
                              const roomBookings =
                                bookings?.filter((b) => {
                                  if (b.roomId !== room.id) return false;
                                  if (b.isCancelled) return false;
                                  return occursOnDate(b, d);
                                }) || [];

                              return (
                                <div className={`flex-1 ${_bg} h-full rounded overflow-hidden relative`} key={room.id}>
                                  {/* Render clickable time segments for this room */}
                                  <div className="absolute inset-0 flex flex-col">
                                    {visibleSegments.map((time, segIdx) => {
                                      const slotShuttered = isTimeSlotShuttered(room, d, time);
                                      return (
                                        <div
                                          key={`${room.id}-${time}-${segIdx}`}
                                          data-time={time}
                                          data-room-id={room.id}
                                          className={`border-b border-gray-200 border-dotted pointer-events-auto relative ${slotShuttered ? "cursor-not-allowed bg-gray-300/40" : "hover:bg-amber-100 hover:cursor-pointer"}`}
                                          style={{
                                            height: `${KEY_HEIGHT}px`,
                                            minHeight: `${KEY_HEIGHT}px`,
                                            maxHeight: `${KEY_HEIGHT}px`,
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTimeSlotClick(e, d, time, room);
                                          }}
                                        >
                                          {slotShuttered && (
                                            <div
                                              className="absolute inset-0 pointer-events-none"
                                              style={{
                                                backgroundImage:
                                                  "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.06) 10px, rgba(0,0,0,0.04) 25px)",
                                              }}
                                            />
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {/* Render bookings for this room on this day */}
                                  {roomBookings.map((booking) => {
                                    // Compute visible segments for correct top if hiddenEarly is enabled
                                    const visibleSegments = hiddenEarly
                                      ? segments.filter((t) => !(hiddenEarly && TIMES_TO_HIDE_EARLY.includes(t)))
                                      : segments;

                                    // Shared placement: a midnight end ("0000") reads as 24:00 and the grid runs to 24:00.
                                    const tile = layoutBookingTile(booking.startTime, booking.endTime, visibleSegments, hiddenEarly, KEY_HEIGHT);
                                    if (!tile) return null;
                                    const { top: _top, height: _height, isClippedAtStart, isClippedAtEnd } = tile;
                                    const half = parseInt((KEY_HEIGHT / 2).toString(), 10);

                                    return (
                                      <div key={booking.id || booking.startTime}>
                                        {hasAnyBuffer(booking) && !isClippedAtStart && !isClippedAtEnd && (
                                          <Buffers
                                            booking={booking}
                                            top={_top}
                                            height={_height}
                                            half={half}
                                            member={member}
                                          />
                                        )}
                                        <Bubble
                                          booking={booking}
                                          member={member}
                                          top={_top}
                                          height={_height}
                                          allowClick
                                          locationSlug={slug}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })}

                            {/* Amenity booking columns */}
                            {selectedAmenities.map((amenity) => {
                              const amenityBookingsForDay = amenityBookings.filter(
                                // Wrap ab.date in new Date() since it comes from JSON as a string
                                (ab) => ab.amenityId === amenity.id && ab.date && isSameDay(new Date(ab.date), d),
                              );

                              return (
                                <AmenityBookingColumn
                                  key={amenity.id}
                                  amenity={amenity}
                                  amenityBookings={amenityBookingsForDay}
                                  date={d}
                                  segments={segments}
                                  hiddenEarly={hiddenEarly}
                                  timesToHideEarly={TIMES_TO_HIDE_EARLY}
                                  keyHeight={KEY_HEIGHT}
                                  member={member}
                                />
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </>
                  </div>
                  <div data-testid="room-schedule-day-footer" className="flex h-16">
                    {weekDates.map((d, i) => {
                      const isToday = sameUtcDay(d, today);
                      return (
                        <div
                          className={`pt-4.5 border-r border-b bg-gray-50 border-gray-200 text-center ${
                            isToday ? "tpr-background px-1 rounded-t text-white" : ""
                          }`}
                          style={{
                            width: `${dynamicDayWidth()}px`,
                            minWidth: `${dynamicDayWidth()}px`,
                            maxWidth: `${dynamicDayWidth()}px`,
                          }}
                          data-testid={`room-schedule-day-header-${format(d, "yyyy-MM-dd")}`}
                          key={i}
                          aria-label={`Day footer showing day date ${format(d, "yyyy-MM-dd")}`}
                        >
                          {formatDayHeader(d)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {roomForInfoModal && selectedLocation && (
        <div className="h-0 hidden invisible">
          <RoomDialog
            open={showRoomInfoModal}
            room={roomForInfoModal}
            location={selectedLocation}
            onOpenChange={setShowRoomInfoModal}
          />
        </div>
      )}

      {/* <CreateBookingDialog
        showBookingDialog={showBookingDialog}
        setShowBookingDialog={setShowBookingDialog}
        bookingResult={bookingResult}
        onBookingCreated={handleBookingSuccess}
      /> */}

      <MakeABookingDialog
        open={showBookingDialog}
        onOpenChange={setShowBookingDialog}
        bookingResult={bookingResult}
        onBookingCreated={refreshBookings}
      />
    </>
  );
};

export default BrickWeekSchedule;
